import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ChevronRight,
  Home,
  Clock,
  ThumbsUp,
  ThumbsDown,
  ChevronLeft,
  Printer,
} from 'lucide-react';
import { MarkdownRenderer } from '@/components/help/MarkdownRenderer';
import { HelpTableOfContents } from '@/components/help/HelpTableOfContents';
import { HelpArticleCard } from '@/components/help/HelpArticleCard';
import { useHelpArticle, useHelpArticles, trackHelpArticleView } from '@/hooks/useHelp';
import { Button } from '@/components/ui/Button';
import { PageLayout } from '@/components/ui/PageLayout';
import { TYPE } from '@/lib/typography';

export function HelpArticle() {
  const { t } = useTranslation();
  const { category: categorySlug, slug: articleSlug } = useParams<{
    category: string;
    slug: string;
  }>();
  const navigate = useNavigate();

  const articleQuery = useHelpArticle(categorySlug, articleSlug);
  const allArticlesQuery = useHelpArticles();

  const article = articleQuery.data;
  const allArticles = allArticlesQuery.data ?? [];

  const [feedbackGiven, setFeedbackGiven] = useState(false);
  const [wasHelpful, setWasHelpful] = useState<boolean | null>(null);

  useEffect(() => {
    if (article) {
      trackHelpArticleView(article.id);
    }
  }, [article?.id]);

  const relatedArticles = allArticles
    .filter((a) => {
      if (!article) return false;
      if (a.id === article.id) return false;
      // Same category or overlapping keywords
      return (
        a.category === article.category ||
        a.search_keywords.some((kw) => article.search_keywords.includes(kw))
      );
    })
    .slice(0, 3);

  const categoryArticles = allArticles.filter((a) => a.category_slug === categorySlug);
  const currentIndex = categoryArticles.findIndex((a) => a.slug === articleSlug);
  const prevArticle = currentIndex > 0 ? categoryArticles[currentIndex - 1] : null;
  const nextArticle =
    currentIndex >= 0 && currentIndex < categoryArticles.length - 1
      ? categoryArticles[currentIndex + 1]
      : null;

  const handleFeedback = (helpful: boolean) => {
    setWasHelpful(helpful);
    setFeedbackGiven(true);

    // TODO: Send feedback to backend
    console.log('Feedback:', helpful);
  };

  const handlePrint = () => {
    window.print();
  };

  if (articleQuery.isLoading || allArticlesQuery.isLoading) {
    return (
      <PageLayout>
        <div className="text-ds-muted">{t('app.loading', 'Indlæser...')}</div>
      </PageLayout>
    );
  }

  if (!article) {
    return (
      <PageLayout title={t('help.home', 'Hjælpecenter')}>
        <div className="rounded-card border border-ds-line bg-ds-card p-s7 text-center">
          <p className="text-ds-muted">{t('help.article_not_found', 'Artikel ikke fundet')}</p>
          <Link to="/help" className="mt-s4 inline-block text-ds-teal hover:text-ds-teal-700">
            {t('help.back_to_center', 'Tilbage til hjælpecenter')}
          </Link>
        </div>
      </PageLayout>
    );
  }

  const readTime = Math.max(1, Math.ceil(article.content.split(/\s+/).length / 200));

  return (
    <PageLayout
      className="print:bg-ds-card"
      title={article.title}
      subtitle={article.excerpt}
      actions={
        <Button variant="secondary" onClick={handlePrint} className="print:hidden">
          <Printer className="h-4 w-4" />
          {t('help.print', 'Udskriv')}
        </Button>
      }
    >
        <nav className={`mb-s5 flex items-center gap-s2 ${TYPE.small} text-ds-muted print:hidden`}>
          <Link to="/help" className="flex items-center gap-s1 hover:text-ds-teal">
            <Home className="h-4 w-4" />
            {t('help.home', 'Hjælpecenter')}
          </Link>
          <ChevronRight className="h-4 w-4" />
          <Link to={`/help/${categorySlug}`} className="hover:text-ds-teal">
            {article.category_title}
          </Link>
          <ChevronRight className="h-4 w-4" />
          <span className="font-medium text-ds-text">{article.title}</span>
        </nav>

        <div className="grid gap-s5 lg:grid-cols-[1fr_250px]">
          <div>
            <p className={`mb-s5 flex items-center gap-s1 ${TYPE.small} text-ds-muted`}>
              <Clock className="h-4 w-4" />
              {readTime} {t('help.min_read', 'min læsning')}
            </p>

            {/* Article content */}
            <div className="rounded-xl border border-ds-line bg-ds-card p-8 shadow-card print:border-0 print:shadow-none">
              <MarkdownRenderer content={article.content} />
            </div>

            {/* Feedback */}
            <div className="mt-8 rounded-xl border border-ds-line bg-ds-card p-6 text-center print:hidden">
              {!feedbackGiven ? (
                <>
                  <p className="mb-4 text-sm font-medium text-ds-text">
                    {t('help.was_helpful', 'Var denne artikel nyttig?')}
                  </p>
                  <div className="flex justify-center gap-3">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleFeedback(true)}
                      className="flex items-center gap-2"
                    >
                      <ThumbsUp className="h-4 w-4" />
                      {t('help.yes', 'Ja')}
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleFeedback(false)}
                      className="flex items-center gap-2"
                    >
                      <ThumbsDown className="h-4 w-4" />
                      {t('help.no', 'Nej')}
                    </Button>
                  </div>
                </>
              ) : (
                <p className="text-sm text-ds-muted">
                  {wasHelpful
                    ? t('help.thanks_helpful', 'Tak for din feedback!')
                    : t(
                        'help.thanks_not_helpful',
                        'Tak for din feedback. Vi arbejder på at forbedre vores guides.',
                      )}
                </p>
              )}
            </div>

            {/* Related articles */}
            {relatedArticles.length > 0 && (
              <div className="mt-12 print:hidden">
                <h2 className="mb-4 text-xl font-semibold text-ds-text">
                  {t('help.related_articles', 'Relaterede artikler')}
                </h2>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {relatedArticles.map((related) => (
                    <HelpArticleCard key={related.id} article={related} showCategory />
                  ))}
                </div>
              </div>
            )}

            {/* Previous / Next navigation */}
            {(prevArticle || nextArticle) && (
              <div className="mt-8 flex justify-between gap-4 print:hidden">
                {prevArticle ? (
                  <button
                    onClick={() => navigate(`/help/${categorySlug}/${prevArticle.slug}`)}
                    className="flex flex-1 items-center gap-2 rounded-lg border border-ds-line bg-ds-card p-4 text-left transition-colors hover:border-ds-teal-line hover:bg-ds-teal-tint"
                  >
                    <ChevronLeft className="h-5 w-5 shrink-0 text-ds-muted" />
                    <div className="min-w-0">
                      <div className="text-xs text-ds-muted">{t('help.previous', 'Forrige')}</div>
                      <div className="truncate text-sm font-medium text-ds-text">
                        {prevArticle.title}
                      </div>
                    </div>
                  </button>
                ) : (
                  <div className="flex-1" />
                )}

                {nextArticle && (
                  <button
                    onClick={() => navigate(`/help/${categorySlug}/${nextArticle.slug}`)}
                    className="flex flex-1 items-center gap-2 rounded-lg border border-ds-line bg-ds-card p-4 text-right transition-colors hover:border-ds-teal-line hover:bg-ds-teal-tint"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-xs text-ds-muted">{t('help.next', 'Næste')}</div>
                      <div className="truncate text-sm font-medium text-ds-text">
                        {nextArticle.title}
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 shrink-0 text-ds-muted" />
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Sidebar: Table of contents */}
          <aside className="print:hidden">
            <HelpTableOfContents content={article.content} />
          </aside>
        </div>
    </PageLayout>
  );
}
